import { useState, useEffect, useMemo, useCallback } from "react";
import { m } from "framer-motion";
import { supabase } from "../../lib/supabase";
import toast from "react-hot-toast";
import { useViewportAnimationTrigger } from "../../hooks/useViewportAnimationTrigger";
import { pageFade } from "../../components/animations/menuAnimations";
import { logger } from "../../utils/logger";

const SECTION_FLAGS = {
  todays_menu: "is_todays_menu",
  daily_specials: "is_daily_special",
  new_dishes: "is_new_dish",
  discount_combos: "is_discount_combo",
} as const;

const formatPrice = (value: number | string): string => {
  try {
    const numeric = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numeric)) return "--";
    return numeric.toFixed(0);
  } catch {
    return "--";
  }
};

interface MenuItem {
  id: string;
  name: string;
  price: number | string;
  is_todays_menu?: boolean | null;
  is_daily_special?: boolean | null;
  is_new_dish?: boolean | null;
  is_discount_combo?: boolean | null;
  is_available?: boolean | null;
  [key: string]: unknown;
}

type SectionKey = keyof typeof SECTION_FLAGS;

const SECTION_LABELS: Record<SectionKey, string> = {
  todays_menu: "Today's Menu",
  daily_specials: "Daily Specials",
  new_dishes: "New Dishes",
  discount_combos: "Discount Combos",
};

const AdminSpecialSections = () => {
  const containerRef = useViewportAnimationTrigger();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [itemBusy, setItemBusy] = useState<Record<string, boolean>>({});
  const [activeSection, setActiveSection] = useState<SectionKey>("todays_menu");

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      // Only select columns that exist in the menu_items table
      const { data: menuItemsData, error: menuItemsError } = await supabase
        .from("menu_items")
        .select("id, name, price, is_available, is_featured")
        .eq("is_available", true)
        .order("name");

      if (menuItemsError) throw menuItemsError;
      // Map to our MenuItem interface - special section flags don't exist in DB yet
      setMenuItems(
        (menuItemsData || []).map((item) => ({
          ...item,
          is_todays_menu: false,
          is_daily_special: false,
          is_new_dish: false,
          is_discount_combo: false,
        })) as MenuItem[],
      );
    } catch (error) {
      logger.error("Error:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();

    const channel = supabase
      .channel("special_sections_live")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "menu_items" },
        loadData,
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadData]);

  const toggleItemSection = async (itemId: string, sectionKey: SectionKey) => {
    const item = menuItems.find((i) => i.id === itemId);
    if (!item) return;

    // Note: These columns don't exist in the database yet
    // This is a placeholder implementation - would need migration to add these columns
    const flagName = SECTION_FLAGS[sectionKey];
    const currentValue = Boolean(item[flagName]);

    setItemBusy((prev) => ({ ...prev, [itemId]: true }));

    try {
      // Update local state only - DB columns don't exist
      setMenuItems((prev) =>
        prev.map((i) =>
          i.id === itemId ? { ...i, [flagName]: !currentValue } : i,
        ),
      );

      toast.success(
        !currentValue
          ? `Added to ${SECTION_LABELS[sectionKey]} (local only)`
          : `Removed from ${SECTION_LABELS[sectionKey]} (local only)`,
      );
    } catch (error) {
      logger.error("Error toggling section:", error);
      toast.error("Failed to update");
    } finally {
      setItemBusy((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return menuItems;
    const term = searchTerm.toLowerCase();
    return menuItems.filter((item) => item.name.toLowerCase().includes(term));
  }, [menuItems, searchTerm]);

  const sectionCounts = useMemo(() => {
    const counts: Record<SectionKey, number> = {
      todays_menu: 0,
      daily_specials: 0,
      new_dishes: 0,
      discount_combos: 0,
    };

    menuItems.forEach((item) => {
      if (item.is_todays_menu) counts.todays_menu++;
      if (item.is_daily_special) counts.daily_specials++;
      if (item.is_new_dish) counts.new_dishes++;
      if (item.is_discount_combo) counts.discount_combos++;
    });

    return counts;
  }, [menuItems]);

  if (loading) {
    return (
      <div className="flex h-full min-h-[400px] items-center justify-center bg-[var(--bg-main)] p-8">
        <div className="text-[var(--text-muted)]">Loading...</div>
      </div>
    );
  }

  return (
    <m.main
      ref={containerRef}
      className="w-full bg-[var(--bg-main)] text-[var(--text-main)]"
      variants={pageFade}
      initial="hidden"
      animate="visible"
      exit="exit"
      style={{ pointerEvents: "auto" }}
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-3 sm:py-4 md:py-5">
        <header className="mb-8">
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-[var(--text-main)]">
            Special Sections
          </h1>
          <p className="mt-2 text-sm sm:text-base text-[var(--text-muted)]">
            Manage which menu items appear in special sections
          </p>
        </header>

        {/* Section Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {(Object.keys(SECTION_FLAGS) as SectionKey[]).map((key) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeSection === key
                  ? "bg-[var(--accent)] text-white"
                  : "bg-theme-elevated border border-theme text-[var(--text-main)] hover:bg-theme"
              }`}
            >
              {SECTION_LABELS[key]} ({sectionCounts[key]})
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search menu items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-md px-4 py-3 bg-theme-elevated border border-theme text-[var(--text-main)] rounded-xl focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent placeholder:text-[var(--text-muted)]"
          />
        </div>

        {/* Items List */}
        <div className="bg-theme-elevated rounded-xl border border-theme p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-[var(--text-main)] mb-4">
            {SECTION_LABELS[activeSection]}
          </h2>

          {filteredItems.length === 0 ? (
            <p className="text-[var(--text-muted)] py-8 text-center">
              No menu items found.
            </p>
          ) : (
            <div className="space-y-2">
              {filteredItems.map((item) => {
                const flagName = SECTION_FLAGS[activeSection];
                const isInSection = Boolean(item[flagName]);
                const isBusy = itemBusy[item.id];

                return (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-theme bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(255,255,255,0.04)]"
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isInSection}
                        disabled={isBusy}
                        onChange={() =>
                          toggleItemSection(item.id, activeSection)
                        }
                        className="w-5 h-5 rounded border-2 border-theme bg-transparent checked:bg-[var(--accent)] checked:border-[var(--accent)] cursor-pointer disabled:opacity-50"
                      />
                      <div>
                        <p className="font-medium text-[var(--text-main)]">
                          {item.name}
                        </p>
                        <p className="text-sm text-[var(--text-muted)]">
                          ৳{formatPrice(item.price)}
                        </p>
                      </div>
                    </div>
                    {isInSection && (
                      <span className="text-xs px-2 py-1 rounded-full bg-[var(--accent)]/20 text-[var(--accent)]">
                        In Section
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </m.main>
  );
};

export default AdminSpecialSections;
